import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import AskAiEmptyState from "./AskAiEmptyState";
import AskAiMessage from "./AskAiMessage";
import LoadingSpinner from "../Common/LoadingSpinner";

function AskAiMessageList({ messages, loading, sending, onSelectQuestion, onRetry, onCancel }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    // jsdom (used by the test suite) does not implement scrollIntoView.
    bottomRef.current?.scrollIntoView?.({ block: "end" });
    // Depends on the last message's length too: a streaming answer
    // grows without changing the message count.
  }, [messages.length, messages[messages.length - 1]?.content?.length, sending]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (messages.length === 0 && !sending) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto">
        <AskAiEmptyState onSelectQuestion={onSelectQuestion} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {messages.map((message, index) => (
        <AskAiMessage
          key={message.message_id || index}
          message={message}
          // Retry re-runs this exact answer in place (see
          // AskAiContext.retryMessage), so it needs the message's own id
          // rather than a copy of the question to resend.
          onRetry={
            message.role === "assistant" && message.status === "failed" && message.message_id
              ? () => onRetry(message.message_id)
              : null
          }
          onCancel={
            message.role === "assistant" && message.status === "pending" && message.message_id
              ? () => onCancel(message.message_id)
              : null
          }
        />
      ))}
      {/* The pending answer is a real message bubble now, which renders
          its own "Thinking…" state. This standalone indicator covers only
          the gap between the question being sent and the server returning
          the assistant row — so it is shown when the thread still ends on
          a user turn. Keying it off "no pending message" instead made a
          second "Thinking…" appear under a just-cancelled answer, because
          cancelling clears the pending status while the poll is still
          winding down. */}
      {sending && (!messages.length || messages[messages.length - 1].role === "user") && (
        <div className="flex justify-start">
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-surface-raised px-4 py-2.5 text-sm text-text-muted">
            <LoadingSpinner size="sm" />
            Thinking…
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

AskAiMessageList.propTypes = {
  messages: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  sending: PropTypes.bool,
  onSelectQuestion: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

AskAiMessageList.defaultProps = {
  loading: false,
  sending: false,
};

export default AskAiMessageList;
